import * as XLSX from "xlsx";
import { format } from "date-fns";

export type SummaryConfig<T> = {
  enabled: boolean;
  sticky?: boolean;
  position?: "top" | "bottom";
  calculations?: {
    average?: (keyof T)[];
    median?: (keyof T)[];
    sum?: (keyof T)[];
    count?: (keyof T)[];
    custom?: {
      column: keyof T;
      label: string;
      calculate: (data: T[]) => any;
    }[];
  };
  rowLabels?: {
    average?: string;
    median?: string;
    sum?: string;
    count?: string;
  };
  excludeColumns?: (keyof T)[];
  labelColumn?: keyof T;
  columnFilters?: {
    [K in keyof T]?: (value: any) => boolean;
  };
};

export const calculateAverage = (arr: number[]): number => {
  const validNumbers = arr.filter(
    (n) => n !== null && n !== undefined && !isNaN(n)
  );

  // console.log("validNumbers.length: ", arr.length);
  return validNumbers.length > 0
    ? validNumbers.reduce((a, b) => a + b, 0) / validNumbers.length
    : 0;
};

export const calculateMedian = (arr: number[]): number => {
  const validNumbers = arr
    .filter((n) => n !== null && n !== undefined && !isNaN(n))
    .sort((a, b) => a - b);
  if (validNumbers.length === 0) return 0;
  const mid = Math.floor(validNumbers.length / 2);
  return validNumbers.length % 2 !== 0
    ? validNumbers[mid]
    : (validNumbers[mid - 1] + validNumbers[mid]) / 2;
};

export const calculateSum = (arr: number[]): number => {
  const validNumbers = arr.filter(
    (n) => n !== null && n !== undefined && !isNaN(n)
  );
  return validNumbers.reduce((a, b) => a + b, 0);
};

export const calculateCount = (arr: any[]): number => {
  return arr.filter(
    (item) => item !== null && item !== undefined && item !== ""
  ).length;
};

export class SummaryCalculationService<T extends Record<string, any>> {
  private data: T[];
  private config: SummaryConfig<T>;
  private formatNumber: (value: any, includeScale?: boolean) => string;

  constructor(
    data: T[],
    config: SummaryConfig<T>,
    formatNumber: (value: any, includeScale?: boolean) => string
  ) {
    this.data = data;
    this.config = config;
    this.formatNumber = formatNumber;
  }

  private getColumnValues(
    columnKey: keyof T,
    applyFilter: boolean = false
  ): number[] {
    return this.data
      .map((row) => {
        const rawValue = row[columnKey];
        let value: number =
          typeof rawValue === "number"
            ? rawValue
            : parseFloat(rawValue as string);

        if (
          String(columnKey) === "dividend_payout" &&
          typeof value === "number" &&
          value < 0
        ) {
          value = 0;
        }

        return value;
      })
      .filter((value): value is number => {
        if (value === null || value === undefined || isNaN(value)) {
          return false;
        }

        if (applyFilter) {
          const filterKey = String(columnKey);
          const columnFilter = (this.config.columnFilters as any)?.[filterKey];
          if (columnFilter && typeof columnFilter === "function") {
            return columnFilter(value);
          }
        }

        return true;
      });
  }

  public calculateSummaryData(): Record<string, any>[] {
    if (!this.config?.enabled || !this.data?.length) return [];

    const { calculations, rowLabels, excludeColumns = [] } = this.config;
    const summaryRows: Record<string, any>[] = [];

    if (calculations?.average && calculations.average.length > 0) {
      const avgRow: Record<string, any> = {};

      if (this.config.labelColumn) {
        avgRow[String(this.config.labelColumn)] =
          rowLabels?.average || "Average";
      } else {
        avgRow["Summary_Type"] = rowLabels?.average || "Average";
      }

      calculations.average.forEach((columnKey) => {
        if (!excludeColumns.includes(columnKey)) {
          const values = this.getColumnValues(columnKey, true);
          const avgValue = calculateAverage(values);
          avgRow[String(columnKey)] = avgValue;
        }
      });

      summaryRows.push(avgRow);
    }

    if (calculations?.median && calculations.median.length > 0) {
      const medianRow: Record<string, any> = {};

      if (this.config.labelColumn) {
        medianRow[String(this.config.labelColumn)] =
          rowLabels?.median || "Median";
      } else {
        medianRow["Summary_Type"] = rowLabels?.median || "Median";
      }

      calculations.median.forEach((columnKey) => {
        if (!excludeColumns.includes(columnKey)) {
          const values = this.getColumnValues(columnKey, true);
          const medianValue = calculateMedian(values);
          medianRow[String(columnKey)] = medianValue;
        }
      });

      summaryRows.push(medianRow);
    }

    if (calculations?.sum && calculations.sum.length > 0) {
      const sumRow: Record<string, any> = {};

      if (this.config.labelColumn) {
        sumRow[String(this.config.labelColumn)] = rowLabels?.sum || "Sum";
      } else {
        sumRow["Summary_Type"] = rowLabels?.sum || "Sum";
      }

      calculations.sum.forEach((columnKey) => {
        if (!excludeColumns.includes(columnKey)) {
          const values = this.getColumnValues(columnKey, false);
          const sumValue = calculateSum(values);
          sumRow[String(columnKey)] = sumValue;
        }
      });

      summaryRows.push(sumRow);
    }

    if (calculations?.count && calculations.count.length > 0) {
      const countRow: Record<string, any> = {};

      if (this.config.labelColumn) {
        countRow[String(this.config.labelColumn)] = rowLabels?.count || "Count";
      } else {
        countRow["Summary_Type"] = rowLabels?.count || "Count";
      }

      calculations.count.forEach((columnKey) => {
        if (!excludeColumns.includes(columnKey)) {
          const values = this.data.map((row) => row[columnKey]);
          const countValue = calculateCount(values);
          countRow[String(columnKey)] = countValue;
        }
      });

      summaryRows.push(countRow);
    }

    if (calculations?.custom && calculations.custom.length > 0) {
      calculations.custom.forEach((customCalc) => {
        const customRow: Record<string, any> = {};

        if (this.config.labelColumn) {
          customRow[String(this.config.labelColumn)] = customCalc.label;
        } else {
          customRow["Summary_Type"] = customCalc.label;
        }

        const customValue = customCalc.calculate(this.data);
        customRow[String(customCalc.column)] = customValue;

        summaryRows.push(customRow);
      });
    }

    return summaryRows;
  }

  public getFormattedSummaryForExcel(): Record<string, any>[] {
    const summaryData = this.calculateSummaryData();

    return summaryData.map((row) => {
      const formattedRow: Record<string, any> = {};

      Object.keys(row).forEach((key) => {
        const value = row[key];

        if (key === String(this.config.labelColumn) || key === "Summary_Type") {
          formattedRow[key] = value;
          return;
        }

        if (typeof value === "number" && !isNaN(value)) {
          const percentageFields = [
            "dividend_yield",
            "dividend_payout",
            "eps_growth",
            "gross_profit_margin",
            "net_profit_margin",
            "operating_cash_margin",
          ];

          const isPercentage = percentageFields.includes(key);
          const shouldIncludeScale = [
            "revenues",
            "gross_profits",
            "net_profits",
            "market_cap",
          ].includes(key);

          let formattedValue = this.formatNumber(value, shouldIncludeScale);
          if (isPercentage) {
            formattedValue += " %";
          }

          formattedRow[key] = formattedValue;
        } else {
          formattedRow[key] = value || "-";
        }
      });

      console.log("formattedRow", formattedRow);

      return formattedRow;
    });
  }
}

export const createExportFunction = (
  data: any[],
  formatNumber: (value: any, includeScale?: boolean) => string,
  summaryConfig: SummaryConfig<any>
) => {
  return () => {
    if (!data || !data.length) {
      alert("No data to export");
      return;
    }

    const exportData = data.map((item: any) => ({
      "Ticker Symbol": item.ticker_symbol || "-",
      "Company Name": item.company_name || "-",
      Sector: item.sector || "-",
      Exchange: item.exchange || "-",
      "Traded Price":
        item.traded_price !== null &&
        item.traded_price !== undefined &&
        !isNaN(item.traded_price)
          ? `${item.currency || ""} ${formatNumber(item.traded_price)}`
          : "-",
      EPS:
        typeof item.eps === "number"
          ? `${item.currency || ""} ${formatNumber(item.eps)}`
          : "0.00",
      "P/E Ratio":
        typeof item.pe_ratio === "number"
          ? item.pe_ratio >= 0
            ? item.pe_ratio > 50
              ? ">50"
              : formatNumber(item.pe_ratio)
            : "NaN"
          : "NaN",
      "Price to Book":
        item.price_to_book !== null &&
        item.price_to_book !== undefined &&
        !isNaN(item.price_to_book)
          ? formatNumber(item.price_to_book)
          : "-",
      "Dividend Yield (%)":
        item.dividend_yield !== null &&
        item.dividend_yield !== undefined &&
        !isNaN(item.dividend_yield)
          ? `${formatNumber(item.dividend_yield)}%`
          : "-",
      "Dividend Payout (%)":
        item.dividend_payout !== null &&
        item.dividend_payout !== undefined &&
        !isNaN(item.dividend_payout)
          ? `${formatNumber(Math.max(0, item.dividend_payout))}%`
          : "-",
      "Debt/Equity":
        item.debt_equity !== null &&
        item.debt_equity !== undefined &&
        !isNaN(item.debt_equity)
          ? formatNumber(item.debt_equity)
          : "-",
      "EPS Growth (%)":
        item.eps_growth !== null &&
        item.eps_growth !== undefined &&
        !isNaN(item.eps_growth)
          ? `${formatNumber(item.eps_growth)}%`
          : "-",
      "Gross Margin (%)":
        item.gross_profit_margin !== null &&
        item.gross_profit_margin !== undefined &&
        !isNaN(item.gross_profit_margin)
          ? `${formatNumber(item.gross_profit_margin)}%`
          : "-",
      "Net Profit Margin (%)":
        item.net_profit_margin !== null &&
        item.net_profit_margin !== undefined &&
        !isNaN(item.net_profit_margin)
          ? `${formatNumber(item.net_profit_margin)}%`
          : "-",
      "Operating Cash Margin (%)":
        item.operating_cash_margin !== null &&
        item.operating_cash_margin !== undefined &&
        !isNaN(item.operating_cash_margin)
          ? `${formatNumber(item.operating_cash_margin)}%`
          : "-",
      Revenue:
        item.revenues !== null &&
        item.revenues !== undefined &&
        !isNaN(item.revenues)
          ? `${item.currency || ""} ${formatNumber(item.revenues, true)}`
          : "-",
      "Gross Profit":
        item.gross_profits !== null &&
        item.gross_profits !== undefined &&
        !isNaN(item.gross_profits)
          ? `${item.currency || ""} ${formatNumber(item.gross_profits, true)}`
          : "-",
      "Net Profit":
        item.net_profits !== null &&
        item.net_profits !== undefined &&
        !isNaN(item.net_profits)
          ? `${item.currency || ""} ${formatNumber(item.net_profits, true)}`
          : "-",
      "Market Capitalization":
        item.market_cap !== null &&
        item.market_cap !== undefined &&
        !isNaN(item.market_cap)
          ? `${item.currency || ""} ${formatNumber(item.market_cap, true)}`
          : "-",
    }));

    const summaryService = new SummaryCalculationService(
      data,
      summaryConfig,
      formatNumber
    );

    const summaryRows = summaryService.getFormattedSummaryForExcel();

    const formattedSummaryRows = summaryRows.map((row) => {
      const mappedRow: Record<string, any> = {};

      const columnMapping: Record<string, string> = {
        ticker_symbol: "Ticker Symbol",
        company_name: "Company Name",
        sector: "Sector",
        exchange: "Exchange",
        traded_price: "Traded Price",
        eps: "EPS",
        pe_ratio: "P/E Ratio",
        price_to_book: "Price to Book",
        dividend_yield: "Dividend Yield (%)",
        dividend_payout: "Dividend Payout (%)",
        debt_equity: "Debt/Equity",
        eps_growth: "EPS Growth (%)",
        gross_profit_margin: "Gross Margin (%)",
        net_profit_margin: "Net Profit Margin (%)",
        operating_cash_margin: "Operating Cash Margin (%)",
        revenues: "Revenue",
        gross_profits: "Gross Profit",
        net_profits: "Net Profit",
        market_cap: "Market Capitalization",
      };

      Object.keys(row).forEach((key) => {
        const excelHeader = columnMapping[key] || key;
        mappedRow[excelHeader] = row[key];
      });

      return mappedRow;
    });

    const allData = [...exportData, ...formattedSummaryRows];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(allData);

    // Set column widths
    const colWidths = [
      { wch: 15 }, // Ticker Symbol
      { wch: 30 }, // Company Name
      { wch: 20 }, // Sector
      { wch: 25 }, // Exchange
      { wch: 15 }, // Traded Price
      { wch: 10 }, // EPS
      { wch: 12 }, // P/E Ratio
      { wch: 15 }, // Price to Book
      { wch: 15 }, // Dividend Yield
      { wch: 15 }, // Dividend Payout
      { wch: 12 }, // Debt/Equity
      { wch: 15 }, // EPS Growth
      { wch: 15 }, // Gross Margin
      { wch: 18 }, // Net Profit Margin
      { wch: 20 }, // Operating Cash Margin
      { wch: 18 }, // Revenue
      { wch: 18 }, // Gross Profit
      { wch: 15 }, // Net Profit
      { wch: 20 }, // Market Capitalization
    ];
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Screener Data");

    const filename = `Stock Screener Data Export_${format(
      new Date(),
      "yyyy-MM-dd"
    )}.xlsx`;

    XLSX.writeFile(wb, filename);
  };
};
